import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { css } from 'styled-components'

import * as Icons from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { createdUpdatedAt } from 'helpers/date/created-updated-at'
import { Icons as helpersIcons } from 'helpers/icons'
import { type ITicket } from 'pages/support/pages/Tickets/types/interfaces/ITicket'

import { TicketStatusZendesk } from '../../../../types/enums/TicketStatus'
import {
    StyledAttachInfo,
    StyledAttachText,
    StyledBottomArea,
    StyledBottomAreaBorder,
    StyledBottomAreaDetails,
    StyledBottomAreaDetailsLeft,
    StyledBottomAreaDetailsRight,
    StyledBottomAreaText,
    StyledDateDate,
    StyledDateText,
    StyledLabelNew,
    StyledMessageInfo,
    StyledMessageText,
    StyledStatus,
    StyledTicketIdInfo,
    StyledTicketIdText
} from '../../common/TicketCard.styled'

interface OpenTicketCardProps {
    ticket: ITicket
    handleDeleteTicket: (ticket: ITicket) => void
}

export const TicketCard = ({
    ticket,
    handleDeleteTicket
}: OpenTicketCardProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const isPendingSync =
        ticket.zendesk_sync_status === 'pending'

    const isFailedSync =
        ticket.zendesk_sync_status === 'failed'

    const displayStatus = isPendingSync
        ? t('waitingForSupport')
        : isFailedSync
          ? t('submissionFailed')
          : ticket.status

    const displayTicketId =
        ticket.zendesk_ticket_id ??
        ticket.public_id ??
        ticket.id

    const handleOpen = () => {
        if (!ticket.public_id) {
            return
        }

        navigate(`/support/ticket/${ticket.public_id}`, {
            state: {
                ticket
            }
        })
    }

    const handleDelete = () => {
        handleDeleteTicket(ticket)
    }

    return (
        <ActionCard
            customStyle={css`
                margin-top: 2rem;
            `}
            icon={helpersIcons.document}
            iconSize='small'
            text={
                <>
                    {t(ticket.subject)}

                    {ticket.status ===
                        TicketStatusZendesk.PENDING &&
                        !isPendingSync && (
                            <StyledLabelNew>
                                {t('new')}
                            </StyledLabelNew>
                        )}
                </>
            }
            subtext={
                <StyledDateText>
                    {t('created')}
                    <StyledDateDate>
                        {createdUpdatedAt(ticket.created_at)}
                    </StyledDateDate>
                </StyledDateText>
            }
            trashFn={handleDelete}
            hasArrow={true}
            onArrowClick={handleOpen}
            bottomArea={
                <StyledBottomArea>
                    <StyledBottomAreaText>
                        {ticket.description}
                    </StyledBottomAreaText>

                    <StyledBottomAreaBorder />

                    <StyledBottomAreaDetails>
                        <StyledBottomAreaDetailsLeft>
                            {(ticket.attachments_count ?? 0) > 0 && (
                                <StyledAttachInfo>
                                    <Icons.IconAttach />

                                    <StyledAttachText>
                                        {ticket.attachments_count}
                                    </StyledAttachText>
                                </StyledAttachInfo>
                            )}

                            {(ticket.comment_count ?? 0) > 0 && (
                                <StyledMessageInfo>
                                    <Icons.IconChatSmall />

                                    <StyledMessageText>
                                        {ticket.comment_count}
                                    </StyledMessageText>
                                </StyledMessageInfo>
                            )}
                        </StyledBottomAreaDetailsLeft>

                        <StyledBottomAreaDetailsRight>
                            <StyledTicketIdInfo>
                                <StyledTicketIdText>
                                    {t('ticket')}
                                </StyledTicketIdText>

                                #{displayTicketId}
                            </StyledTicketIdInfo>

                            <StyledStatus
                                $isOpen={
                                    isPendingSync ||
                                    ticket.status ===
                                        TicketStatusZendesk.OPEN
                                }
                            >
                                {displayStatus}
                            </StyledStatus>
                        </StyledBottomAreaDetailsRight>
                    </StyledBottomAreaDetails>
                </StyledBottomArea>
            }
        />
    )
}